/* eslint-disable camelcase */
/**
 * Product-comparison analytics (GAI-5969).
 *
 * Event 1 — Comparison Impression (batched): each comparison product card seen
 * during the session (>= VISIBILITY_THRESHOLD of the card visible) is cached,
 * keyed by the AI response `message_id` of its comparison table. On chatbot
 * close the cache is flushed as a SINGLE SOT event per comparison table, with
 * every seen product's fields semicolon-joined.
 *
 * Dedupe: a product (by SKU) from the same message is cached at most once per
 * session. Only fresh comparisons register impressions — restored/history
 * frames pass `isPrevious` and never call in (guarded at the card).
 */
import processEvent from 'analytics/processEvent';
import anaConsts from 'analytics/constants';
import anaUtils from 'analytics/utils';
import { ensureFreshReferrer } from 'ai/analytics/utils/superChatAnalyticsContext';
import { EVENT_NAMES } from 'ai/analytics/events';
import { buildComparisonImpressionFields } from 'ai/analytics/utils/sotFields';

// Map<messageId, { sessionId, messageId, referrer, products: Map<skuId, { skuId, productId, productName, brandName }> }>
const comparisonImpressionCache = new Map();

// GAI-7430 AC3: the comparison "swipe" (arrow) interaction is reported at most once
// per AIBC session. This module-level guard is reset on session teardown via
// resetComparisonSwipeTracking (wired in useSuperChatSocket alongside the other resets).
let hasTrackedComparisonSwipe = false;

/**
 * GAI-7430 AC1: fires an Adobe Analytics async page load (s.t() / TrackState) when the
 * product comparison card is rendered in beauty chat. Sets `pageName` to the comparison
 * page identifier. Should be called once per card render (caller is responsible for guarding).
 *
 * @returns {void}
 */
export function trackComparisonCardView() {
    processEvent.process(anaConsts.ASYNC_PAGE_LOAD, {
        data: { pageName: anaConsts.SUPER_CHAT.SUPERCHAT_COMPARISON }
    });
}

/**
 * GAI-7430 AC2: records the comparison add-to-basket page context so the interaction
 * attributes to `superchat:compare product`. The actual `scAdd` link event (s.tl() /
 * TrackAction) is fired by addToBasketEvent using the analyticsData built in
 * buildBasketAnalyticsData (the `isComparison` branch); this mirrors
 * trackShadeFinderAddToBasket by storing next-page data (or firing directly when
 * `useNextPageData` is false).
 *
 * @param {string} skuId - SKU id of the comparison product being added.
 * @param {object} [options]
 * @param {boolean} [options.useNextPageData=false] - store next-page data instead of firing now.
 * @returns {void}
 */
export function trackComparisonAddToBasket(skuId, options = {}) {
    const { useNextPageData = false } = options;

    const data = {
        pageName: anaConsts.SUPER_CHAT.SUPERCHAT_COMPARISON,
        actionInfo: anaConsts.SUPER_CHAT.SUPERCHAT_COMPARISON_BASKET,
        eventStrings: 'scAdd',
        productStrings: `;${skuId};;;;eVar26=${skuId}|eVar23=superchat|eVar72=superchat|eVar133=standard`
    };

    if (useNextPageData) {
        anaUtils.setNextPageData(data);
    } else {
        processEvent.process(anaConsts.LINK_TRACKING_EVENT, { data });
    }
}

/**
 * GAI-7430 AC3: fires an Adobe Analytics link tracking event (s.tl() / TrackAction) the
 * first time a user swipes the comparison card (clicks a nav arrow). Reported at most
 * once per AIBC session — subsequent swipes are no-ops until the session resets.
 *
 * Adobe variables set:
 *   - pageName / eVar70 / eVar97: `superchat:compare product:n/a:*`
 *   - prop55 (actionInfo): `superchat:compare product:swipe`
 *
 * @returns {void}
 */
export function trackComparisonSwipe() {
    if (hasTrackedComparisonSwipe) {
        return;
    }

    hasTrackedComparisonSwipe = true;

    processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
        data: {
            pageName: anaConsts.SUPER_CHAT.SUPERCHAT_COMPARISON,
            actionInfo: anaConsts.SUPER_CHAT.SUPERCHAT_COMPARISON_SWIPE
        }
    });
}

/**
 * Reset the once-per-session comparison swipe guard (new conversation / session reset).
 *
 * @returns {void}
 */
export function resetComparisonSwipeTracking() {
    hasTrackedComparisonSwipe = false;
}

/**
 * Record that a comparison product card was seen. Deduplicated by SKU within the
 * same comparison table (message).
 *
 * @param {object} params
 * @param {string} params.messageId comparison table AI response message id
 * @param {string} params.sessionId beautyChatSessionId
 * @param {object} params.product raw comparison product (snake_case fields)
 * @returns {void}
 */
export function cacheComparisonProductImpression({ messageId, sessionId, product } = {}) {
    const skuId = product?.default_sku;

    if (!messageId || !skuId) {
        return;
    }

    let entry = comparisonImpressionCache.get(messageId);

    if (!entry) {
        // Capture the referrer (sotV425) when the comparison is first SEEN, not at
        // flush time — the referrer is only persisted on card interactions, so a
        // view-only session would otherwise flush without it. ensureFreshReferrer
        // always computes and returns a value (at minimum `superchat:other`).
        entry = {
            sessionId,
            messageId,
            referrer: ensureFreshReferrer(),
            products: new Map()
        };
        comparisonImpressionCache.set(messageId, entry);
    } else {
        // Backfill session id / referrer if the first impression arrived before
        // they were available, so the flushed event still carries sotV450/sotV425.
        if (!entry.sessionId && sessionId) {
            entry.sessionId = sessionId;
        }

        if (!entry.referrer) {
            entry.referrer = ensureFreshReferrer();
        }
    }

    if (entry.products.has(skuId)) {
        return;
    }

    entry.products.set(skuId, {
        skuId,
        productId: product.id,
        productName: product.title,
        brandName: product.brand_name
    });
}

/**
 * Fire one batched SOT impression event per cached comparison table, then clear
 * the cache. Called when the chatbot is closed/dismissed.
 *
 * @returns {void}
 */
export function flushComparisonImpressions() {
    if (comparisonImpressionCache.size === 0) {
        return;
    }

    const eventName = EVENT_NAMES.COMPARISON_IMPRESSION;

    comparisonImpressionCache.forEach(entry => {
        const products = Array.from(entry.products.values());

        if (products.length === 0) {
            return;
        }

        const sotFields = buildComparisonImpressionFields({
            sessionId: entry.sessionId,
            messageId: entry.messageId,
            referrer: entry.referrer,
            products
        });

        processEvent.process(anaConsts.SOT_LINK_TRACKING_EVENT, {
            data: {
                linkName: eventName,
                actionInfo: eventName,
                specificEventName: eventName,
                sotType: eventName,
                ...sotFields
            }
        });
    });

    comparisonImpressionCache.clear();
}

/**
 * Clear the impression cache without firing (new conversation / session reset).
 *
 * @returns {void}
 */
export function resetComparisonImpressionCache() {
    comparisonImpressionCache.clear();
}
